// CSS imports - order matters for cascade
import '../css/base.css';
import '../css/utilities.css';
import '../css/components.css';
import '../css/layout.css';
import '../css/views.css';
import '../css/homepage.css';

import { store } from './state';
import { addRoute, initRouter, navigate } from './router';
import { connect as connectSocket } from './socket';
import { profileApi, preferencesApi, gendersApi, relationshipStatusesApi, searchApi, matchesApi } from './api';
import { renderLogin, renderWelcome, renderWelcome2 } from './views/login';
import { renderOnboarding } from './views/onboarding';
import { renderBrowse, renderLikes } from './views/browse';
import { renderFilters } from './views/filters';
import { renderMatches } from './views/matches';
import { renderChat } from './views/chat';
import { renderProfile } from './views/profile';
import { renderProfileView } from './views/profile-view';
import { renderPrivacy } from './views/privacy';
import { renderTerms } from './views/terms';
import { renderContact } from './views/contact';
import { renderCompare } from './views/compare';
import { renderQuestions } from './views/questions';
import { renderQuestionSet } from './views/question-set';
import { renderAdmin } from './views/admin';
import { renderKnowledge } from './views/knowledge';
import { TOAST_DISPLAY_DURATION_MS, isNativePlatform } from './config';
import { initTheme } from './theme';
import { initKeyboard } from './utils/keyboard';
import { countUnansweredFromDOM, countUnansweredQuestions } from './options';

const app = document.getElementById('app')!;

// Likes count for badge (cached)
let likesCount = 0;

// Chats unread count for badge (cached)
let chatsCount = 0;

// Browse result count for badge (cached)
let browseCount = 0;

// Profile incomplete count for badge (cached)
let profileIncompleteCount = 0;

// Fetch likes count and update badge
export async function refreshLikesCount(): Promise<void> {
  if (!store.isAuthenticated()) return;
  try {
    const response = await searchApi.getLikesCount();
    likesCount = response.data.count;
    updateBadge('likes-badge', likesCount);
  } catch {
    // Silently fail - don't block UI
  }
}

// Update a nav badge in the DOM
function updateBadge(id: string, count: number): void {
  const badge = document.getElementById(id);
  if (badge) {
    const label = count > 999 ? '999+' : count > 0 ? String(count) : '';
    badge.textContent = label;
    badge.style.display = count > 0 ? 'inline' : 'none';
  }
}

// Update browse result count badge
export function updateBrowseCount(count: number): void {
  browseCount = count;
  updateBadge('browse-badge', browseCount);
}

// Fetch chats unread count and update badge
export async function refreshChatsCount(): Promise<void> {
  if (!store.isAuthenticated()) return;
  try {
    const response = await matchesApi.getUnreadCount();
    chatsCount = response.data.count;
    updateBadge('chats-badge', chatsCount);
  } catch {
    // Silently fail - don't block UI
  }
}

// Refresh profile incomplete count - prefer live DOM when on profile page
export function refreshProfileIncompleteCount(): void {
  if (document.getElementById('tab-about')) {
    // Profile page - count from live DOM
    profileIncompleteCount = countUnansweredFromDOM();
  } else {
    // Other pages - fall back to store data
    const user = store.getState().user;
    profileIncompleteCount = countUnansweredQuestions(user?.profile);
  }
  updateBadge('profile-badge', profileIncompleteCount);
}

// Navigation bar HTML
function renderNav(): string {
  const currentView = store.getState().currentView;

  // Check if on browse-related routes
  const isBrowseActive = currentView.startsWith('/browse') ||
    currentView.startsWith('/u/') ||
    currentView.startsWith('/filters');

  const isLikesActive = currentView === '/likes';

  return `
    <nav class="navbar">
      <a href="#/browse" class="nav-item ${isBrowseActive ? 'active' : ''}">
        <i class="fas fa-people-group"></i>
        <span>Browse</span>
        <span class="nav-badge" id="browse-badge" style="display: ${browseCount > 0 ? 'inline' : 'none'}">${browseCount > 0 ? (browseCount > 999 ? '999+' : browseCount) : ''}</span>
      </a>
      <a href="#/likes" class="nav-item ${isLikesActive ? 'active' : ''}">
        <i class="fas fa-circle-check"></i>
        <span>Interested</span>
        <span class="nav-badge" id="likes-badge" style="display: ${likesCount > 0 ? 'inline' : 'none'}">${likesCount > 0 ? likesCount : ''}</span>
      </a>
      <a href="#/matches" class="nav-item ${currentView.startsWith('/matches') || currentView.startsWith('/chat') ? 'active' : ''}">
        <i class="fas fa-message"></i>
        <span>Matches</span>
        <span class="nav-badge" id="chats-badge" style="display: ${chatsCount > 0 ? 'inline' : 'none'}">${chatsCount > 0 ? chatsCount : ''}</span>
      </a>
      <a href="#/about-me" class="nav-item ${['/about-me', '/my-photos', '/view-profile', '/settings'].includes(currentView) ? 'active' : ''}">
        <i class="fas fa-user"></i>
        <span>Profile</span>
        <span class="nav-badge" id="profile-badge" style="display: ${profileIncompleteCount > 0 ? 'inline' : 'none'}">${profileIncompleteCount > 0 ? profileIncompleteCount : ''}</span>
      </a>
    </nav>
  `;
}

// Render function that includes nav for authenticated views
export function render(content: string, includeNav = true): void {
  if (includeNav && store.isAuthenticated()) {
    app.innerHTML = `<div class="page">${content}</div>${renderNav()}`;
  } else {
    app.innerHTML = content;
  }
}

// Toast notification
export function showToast(message: string, type: 'success' | 'error' = 'success', durationMs?: number): void {
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.style.whiteSpace = 'pre-line';
  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.classList.add('fade-out');
    toast.addEventListener('animationend', () => toast.remove());
  }, durationMs ?? TOAST_DISPLAY_DURATION_MS);
}

// Show an API error as a toast, extracting the server message if available
export function showApiError(err: unknown, fallback: string): void {
  const message = (err as any)?.response?.data?.error || fallback;
  showToast(message, 'error');
}

// Loading state
export function showLoading(): void {
  app.innerHTML = `
    <div class="loading">
      <div class="spinner"></div>
    </div>
  `;
}

// Load user data after authentication
async function loadUserData(): Promise<void> {
  try {
    // Load genders and relationship statuses first (needed for profile/preferences)
    const [gendersRes, relationshipStatusesRes] = await Promise.all([
      gendersApi.getAll(),
      relationshipStatusesApi.getAll(),
    ]);
    store.setGenders(gendersRes.data);
    store.setRelationshipStatuses(relationshipStatusesRes.data);

    // Load profile and preferences
    const [profileRes, prefsRes] = await Promise.all([
      profileApi.get().catch(() => null),
      preferencesApi.get().catch(() => null),
    ]);

    if (profileRes?.data) {
      const state = store.getState();
      store.setUser({
        id: profileRes.data.user_id,
        email: profileRes.data.email || '',
        phone_number: profileRes.data.phone_number || null,
        email_verified: profileRes.data.email_verified || false,
        profile: profileRes.data,
        preferences: prefsRes?.data || null,
      });
    }
  } catch (err) {
    console.error('Failed to load user data:', err);
  }
}

// Initialize Capacitor plugins
async function initCapacitor(): Promise<void> {
  // Initialize theme (works on both web and native)
  initTheme();

  // Initialize keyboard handling (native only, no-op on web)
  await initKeyboard();

  // Hide splash screen on native platforms
  if (isNativePlatform()) {
    try {
      const { SplashScreen } = await import('@capacitor/splash-screen');
      await SplashScreen.hide({ fadeOutDuration: 300 });
    } catch (err) {
      console.warn('Could not hide splash screen:', err);
    }
  }
}

// Initialize app
async function init(): Promise<void> {
  // Initialize Capacitor plugins first
  await initCapacitor();

  // Set up routes
  addRoute('/welcome', renderWelcome, false);
  addRoute('/welcome2', renderWelcome2, false);
  addRoute('/login', renderLogin, false);
  addRoute('/privacy', renderPrivacy, false);
  addRoute('/terms', renderTerms, false);
  addRoute('/contact', renderContact, false);
  addRoute('/compare', renderCompare, false);
  addRoute('/onboarding', renderOnboarding, true);
  addRoute('/browse', renderBrowse, true);
  addRoute('/likes', renderLikes, true);
  addRoute('/filters', renderFilters, true);
  addRoute('/matches', renderMatches, true);
  addRoute('/questions', renderQuestions, true);
  addRoute('/questions/:id', renderQuestionSet, true);
  addRoute('/chat/:id/answers/:setId', renderChat, true);
  addRoute('/chat/:id/answers', renderChat, true);
  addRoute('/chat/:id', renderChat, true);
  addRoute('/about-me', renderProfile, true);
  addRoute('/my-photos', renderProfile, true);
  addRoute('/view-profile', renderProfile, true);
  addRoute('/settings', renderProfile, true);
  addRoute('/profile/:id', renderProfileView, true);
  addRoute('/u/:id', renderProfileView, true);  // New short URL for profile view
  addRoute('/knowledge', renderKnowledge, true);
  addRoute('/admin', renderAdmin, true);
  addRoute('/admin/:tab', renderAdmin, true);

  // If authenticated, load user data and connect socket
  if (store.isAuthenticated()) {
    showLoading();
    await loadUserData();
    connectSocket();
    // Initialize push notifications on native (non-blocking)
    import('./push').then(m => m.initPush()).catch(() => {});
    // Initialize analytics (non-blocking)
    import('./analytics').then(m => m.initAnalytics()).catch(() => {});
    // Fetch counts for badges (non-blocking)
    refreshLikesCount();
    refreshChatsCount();
    refreshProfileIncompleteCount();
  }

  // Initialize router
  initRouter();

  // Re-render nav when state changes
  store.subscribe(() => {
    const nav = document.querySelector('.navbar');
    if (nav && store.isAuthenticated()) {
      // Update profile incomplete count from current state
      refreshProfileIncompleteCount();
      nav.outerHTML = renderNav();
    }
  });
}

// Export for views to use
export { loadUserData, renderNav };

// Start app
init();
